/**
 * Panneau de statistiques pour la délibération finale.
 * - Taux de participation aux examens (session ordinaire + rattrapage) :
 *   présence = au moins 40 % des matières d'examen hors TP composées (copie associée ou note > 0)
 * - Périmètre = filtre « Type formation » du tableau (FI + DD par défaut, CS, TOUS…)
 * - Taux d'admission par session (ResultatSemestre.session_validation)
 * - Simulation de rachat : ajournés dont la moyenne >= seuil choisi
 *
 * Hérite des filtres de la page TableauDeliberation (pôle, option, classe…).
 */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  BarChart3, Users, CheckCircle2, AlertCircle, Repeat, LifeBuoy, Loader2, Printer, GraduationCap,
} from 'lucide-react';
import {
  useGetStatsDeliberationQuery, StatsDeliberationParams, StatsDeliberationResponse,
} from '../../services/resultatsApi';
import { API_URL, getAnneeAcademique } from '../../services/baseApi';
import { Cycle, SelecteurCycle } from '../../components/ui/SelecteurCycle';
import { useGetInfoInstitutQuery } from '../../services/coreApi';
import { fmtPct, genererPdfStatsDeliberation, TypeFormationStats } from './statsDeliberationPdf';
import { genererPdfSyntheseDD } from './syntheseDoubleDiplomePdf';

const LIB_TF: Record<TypeFormationStats, string> = {
  FI_DD: 'FI + DD', FI: 'FI', DD: 'DD', CS: 'Cours du soir', TOUS: 'Toutes formations',
};

interface Props {
  semestre: 1 | 2;
  annee?: string;
  pole?: string;
  option?: string;
  classe?: string;
  niveau?: string;
  typeFormation?: 'FI_DD' | 'FI' | 'DD' | 'CS' | 'TOUS';
}

const ProgressBar: React.FC<{ pct: number; color: string }> = ({ pct, color }) => (
  <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
    <div className={`h-full ${color}`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
  </div>
);

const StatLine: React.FC<{ label: React.ReactNode; value: React.ReactNode; muted?: boolean }> = ({
  label, value, muted,
}) => (
  <div className="flex items-baseline justify-between gap-2 text-xs">
    <span className={muted ? 'text-gray-500 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'}>{label}</span>
    <span className={`font-mono font-semibold ${muted ? 'text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>
      {value}
    </span>
  </div>
);

const StatsDeliberation: React.FC<Props> = ({
  semestre, annee, pole, option, classe, niveau, typeFormation = 'FI_DD',
}) => {
  const [seuil, setSeuil] = useState<number>(9);
  // Sans niveau ni classe, licence et master sont présentés séparément (2026-10-02).
  const [cycle, setCycle] = useState<Cycle>('licence');
  const sansNiveau = !niveau && !classe;
  // Fix 2026-09-18 : les statistiques suivent le filtre « Type formation » du
  // tableau. Avant, CS et TOUS étaient rabattus sur FI + DD, et les inscrits /
  // participants ne bougeaient pas quand on sélectionnait les cours du soir.
  const tfStats: TypeFormationStats = typeFormation || 'FI_DD';
  const { data: infoInstitut } = useGetInfoInstitutQuery();

  const params: StatsDeliberationParams = useMemo(() => ({
    semestre,
    annee: annee || undefined,
    pole: pole || undefined,
    option: option || undefined,
    classe: classe || undefined,
    niveau: niveau || undefined,
    cycle: sansNiveau ? cycle : undefined,
    type_formation: tfStats,
    seuil_rachat: seuil,
  }), [semestre, annee, pole, option, classe, niveau, tfStats, seuil, sansNiveau, cycle]);

  const { data, isFetching, error } = useGetStatsDeliberationQuery(params);

  // ── État popover d'impression PDF ──────────────────────────────
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [printOrdinaire, setPrintOrdinaire] = useState(true);
  const [printRattrapage, setPrintRattrapage] = useState(true);
  const [printRachat, setPrintRachat] = useState(true);
  const [printSemestreOpt, setPrintSemestreOpt] = useState<'courant' | 'S1' | 'S2' | 'both'>('courant');
  const [printLoading, setPrintLoading] = useState(false);
  const [ddLoading, setDdLoading] = useState(false);
  const printMenuRef = useRef<HTMLDivElement>(null);

  // ── Synthèse Double Diplôme (PDF, en-tête 2i + ISPI) ──────────────
  const exportSyntheseDD = async () => {
    setDdLoading(true);
    try {
      const usp = new URLSearchParams();
      if (annee) usp.set('annee', annee);
      if (pole) usp.set('pole', pole);
      if (niveau) usp.set('niveau', niveau);
      if (option) usp.set('option', option);
      const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token') || '';
      const anneeHeader = getAnneeAcademique();
      const res = await fetch(`${API_URL}/resultats-semestre/synthese-double-diplome/?${usp.toString()}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(anneeHeader ? { 'X-Annee-Academique': anneeHeader } : {}),
        },
      });
      if (!res.ok) { alert('Impossible de charger la synthèse Double Diplôme.'); return; }
      const dd = await res.json();
      if (!dd.effectif) { alert('Aucun étudiant Double Diplôme dans ce périmètre.'); return; }
      const scopeSuffix = (option || niveau || pole || 'global').replace(/[^A-Za-z0-9]+/g, '_');
      await genererPdfSyntheseDD({
        data: dd,
        perimetre: { pole, niveau, option },
        institut: {
          ville: infoInstitut?.ville,
          signataireTitre: infoInstitut?.chef_scolarite_titre,
          signataireNom: infoInstitut?.chef_scolarite,
        },
        nomFichier: `Synthese_Double_Diplome_${scopeSuffix}.pdf`,
      });
    } finally {
      setDdLoading(false);
    }
  };

  // Fermer le popover au clic extérieur
  useEffect(() => {
    if (!showPrintMenu) return;
    const onClick = (e: MouseEvent) => {
      if (printMenuRef.current && !printMenuRef.current.contains(e.target as Node)) {
        setShowPrintMenu(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showPrintMenu]);

  // Récupère les stats pour un semestre donné (fetch direct pour pouvoir
  // demander des semestres autres que celui actuellement affiché).
  const fetchStatsPour = async (sem: 1 | 2): Promise<StatsDeliberationResponse | null> => {
    if (sem === semestre && data) return data;
    try {
      const usp = new URLSearchParams();
      usp.set('semestre', String(sem));
      if (annee) usp.set('annee', annee);
      if (pole) usp.set('pole', pole);
      if (option) usp.set('option', option);
      if (classe) usp.set('classe', classe);
      if (niveau) usp.set('niveau', niveau);
      if (sansNiveau) usp.set('cycle', cycle);
      usp.set('type_formation', tfStats);
      usp.set('seuil_rachat', String(seuil));
      const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token') || '';
      const res = await fetch(`${API_URL}/resultats-semestre/stats-deliberation/?${usp.toString()}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch { return null; }
  };

  const exportPDF = async () => {
    setPrintLoading(true);
    try {
      // 1. Déterminer les semestres à inclure
      let semestres: (1 | 2)[];
      if (printSemestreOpt === 'both') semestres = [1, 2];
      else if (printSemestreOpt === 'S1') semestres = [1];
      else if (printSemestreOpt === 'S2') semestres = [2];
      else semestres = [semestre];

      // 2. Charger les données pour chaque semestre
      const datasets: Array<{ sem: 1 | 2; stats: StatsDeliberationResponse | null }> = [];
      for (const s of semestres) {
        const stats = await fetchStatsPour(s);
        datasets.push({ sem: s, stats });
      }

      // 3. Construire le PDF (mise en page : statsDeliberationPdf.ts)
      const semSuffix = printSemestreOpt === 'both' ? 'S1S2'
        : printSemestreOpt === 'S1' ? 'S1'
        : printSemestreOpt === 'S2' ? 'S2'
        : `S${semestre}`;
      const scopeSuffix = (classe || option || niveau || pole || 'global').replace(/[^A-Za-z0-9]+/g, '_');
      await genererPdfStatsDeliberation({
        datasets,
        annee: data?.annee || annee,
        perimetre: {
          pole, option, classe,
          niveau: niveau || (sansNiveau ? (cycle === 'master' ? 'M1-M2 (master)' : 'L1 à L3 (licence)') : undefined),
        },
        typeFormation: tfStats,
        inclureOrdinaire: printOrdinaire,
        inclureRattrapage: printRattrapage,
        inclureRachat: printRachat,
        seuilRachat: seuil,
        institut: {
          ville: infoInstitut?.ville,
          signataireTitre: infoInstitut?.chef_scolarite_titre,
          signataireNom: infoInstitut?.chef_scolarite,
        },
        nomFichier: `Statistiques_deliberation_${semSuffix}_${scopeSuffix}.pdf`,
      });
      setShowPrintMenu(false);
    } finally {
      setPrintLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-3">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2">
          <BarChart3 size={16} className="text-blue-600 dark:text-blue-400" />
          <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">
            Statistiques de délibération
          </h3>
          {isFetching && <Loader2 size={14} className="animate-spin text-gray-400" />}
        </div>
        <div className="flex items-center gap-2">
          {sansNiveau && <SelecteurCycle value={cycle} onChange={setCycle} taille="sm" />}
          <div className="text-[11px] text-gray-500">
            Semestre {semestre} · {LIB_TF[tfStats]}
            {classe ? ` · ${classe}` : (niveau ? ` · niveau ${niveau}` : '')}
            {option ? ` · ${option}` : ''}
          </div>
          <button
            onClick={exportSyntheseDD}
            disabled={ddLoading}
            className="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-medium rounded inline-flex items-center gap-1"
            title="Synthèse Double Diplôme (PDF, en-tête 2i + ISPI) — moyennes FI, DD et générale"
          >
            {ddLoading ? <Loader2 size={12} className="animate-spin" /> : <GraduationCap size={12} />} Synthèse DD
          </button>
          <div className="relative" ref={printMenuRef}>
            <button
              onClick={() => setShowPrintMenu(v => !v)}
              disabled={!data || data.effectif_inscrits === 0}
              className="px-2.5 py-1 bg-gray-700 hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-medium rounded inline-flex items-center gap-1"
              title="Imprimer les statistiques en PDF"
            >
              <Printer size={12} /> Imprimer
            </button>
            {showPrintMenu && (
              <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-3 z-50">
                <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Sessions à inclure
                </div>
                <div className="space-y-1.5 mb-3">
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={printOrdinaire}
                      onChange={(e) => setPrintOrdinaire(e.target.checked)}
                      className="rounded text-blue-600" />
                    <span className="text-gray-700 dark:text-gray-300">Session ordinaire</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={printRattrapage}
                      onChange={(e) => setPrintRattrapage(e.target.checked)}
                      className="rounded text-orange-600" />
                    <span className="text-gray-700 dark:text-gray-300">Session de rattrapage</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={printRachat}
                      onChange={(e) => setPrintRachat(e.target.checked)}
                      className="rounded text-purple-600" />
                    <span className="text-gray-700 dark:text-gray-300">
                      Simulation rachat (seuil {seuil.toFixed(2)})
                    </span>
                  </label>
                </div>

                <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Semestre(s)
                </div>
                <div className="grid grid-cols-2 gap-1 mb-3">
                  <label className="flex items-center gap-1.5 text-[11px] cursor-pointer p-1.5 border border-gray-200 dark:border-gray-700 rounded">
                    <input type="radio" name="prt_sem" checked={printSemestreOpt === 'courant'}
                      onChange={() => setPrintSemestreOpt('courant')} />
                    Semestre courant (S{semestre})
                  </label>
                  <label className="flex items-center gap-1.5 text-[11px] cursor-pointer p-1.5 border border-gray-200 dark:border-gray-700 rounded">
                    <input type="radio" name="prt_sem" checked={printSemestreOpt === 'both'}
                      onChange={() => setPrintSemestreOpt('both')} />
                    Les deux (S1 + S2)
                  </label>
                  <label className="flex items-center gap-1.5 text-[11px] cursor-pointer p-1.5 border border-gray-200 dark:border-gray-700 rounded">
                    <input type="radio" name="prt_sem" checked={printSemestreOpt === 'S1'}
                      onChange={() => setPrintSemestreOpt('S1')} />
                    S1 uniquement (impair)
                  </label>
                  <label className="flex items-center gap-1.5 text-[11px] cursor-pointer p-1.5 border border-gray-200 dark:border-gray-700 rounded">
                    <input type="radio" name="prt_sem" checked={printSemestreOpt === 'S2'}
                      onChange={() => setPrintSemestreOpt('S2')} />
                    S2 uniquement (pair)
                  </label>
                </div>

                <button
                  onClick={exportPDF}
                  disabled={printLoading || (!printOrdinaire && !printRattrapage && !printRachat)}
                  className="w-full py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-semibold rounded inline-flex items-center justify-center gap-1.5"
                >
                  {printLoading
                    ? <><Loader2 size={12} className="animate-spin" /> Génération…</>
                    : <><Printer size={12} /> Générer le PDF</>}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="text-xs text-red-600 dark:text-red-400 mb-2">
          Erreur de chargement des statistiques
        </div>
      )}

      {data && data.effectif_inscrits === 0 && (
        <div className="text-xs text-gray-500 italic py-4 text-center">
          Aucun étudiant dans ce périmètre.
        </div>
      )}

      {data && data.effectif_inscrits > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* ─── Carte 1 : Session Ordinaire ─── */}
            <div className="border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10 rounded p-3 space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 dark:text-blue-300">
                <Users size={13} /> Session ordinaire
              </div>
              <div className="space-y-1">
                <StatLine label="Inscrits" value={data.effectif_inscrits} />
                <StatLine label="Participants" value={data.session_ordinaire?.nb_participants ?? 0} />
                <StatLine label="Absents" value={data.session_ordinaire?.nb_absents ?? 0} muted />
              </div>
              <div>
                <div className="flex items-baseline justify-between text-[11px] mb-0.5">
                  <span className="text-gray-600 dark:text-gray-400">Taux participation</span>
                  <span className="font-mono font-bold text-blue-700 dark:text-blue-300">
                    {fmtPct(data.session_ordinaire?.taux_participation)}
                  </span>
                </div>
                <ProgressBar pct={data.session_ordinaire?.taux_participation ?? 0} color="bg-blue-500" />
              </div>
              <div className="pt-1 border-t border-blue-200 dark:border-blue-800 space-y-1">
                <StatLine
                  label={<><CheckCircle2 size={11} className="inline mr-1 text-green-600" />Admis</>}
                  value={data.session_ordinaire?.nb_admis ?? 0}
                />
                <StatLine
                  label={<><AlertCircle size={11} className="inline mr-1 text-red-600" />Ajournés à l'issue de l'ordinaire</>}
                  value={data.session_ordinaire?.nb_ajournes ?? 0}
                  muted
                />
                {(data.nb_sans_resultat ?? 0) > 0 && (
                  <StatLine label="Sans résultat (non calculé)" value={data.nb_sans_resultat} muted />
                )}
              </div>
              <div>
                <div className="flex items-baseline justify-between text-[11px] mb-0.5">
                  <span className="text-gray-600 dark:text-gray-400">Taux admission</span>
                  <span className="font-mono font-bold text-green-700 dark:text-green-300">
                    {fmtPct(data.session_ordinaire?.taux_admission)}
                  </span>
                </div>
                <ProgressBar pct={data.session_ordinaire?.taux_admission ?? 0} color="bg-green-500" />
                {(data.session_rattrapage?.nb_admis ?? 0) > 0 && data.taux_admission_total !== undefined && (
                  <div className="flex items-baseline justify-between text-[11px] mt-1">
                    <span className="text-gray-500 dark:text-gray-500">Admission cumulée avec rattrapage</span>
                    <span className="font-mono font-semibold text-gray-700 dark:text-gray-300">
                      {fmtPct(data.taux_admission_total)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* ─── Carte 2 : Session Rattrapage ─── */}
            <div className="border border-orange-200 dark:border-orange-800 bg-orange-50/40 dark:bg-orange-900/10 rounded p-3 space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-orange-700 dark:text-orange-300">
                <Repeat size={13} /> Session rattrapage
              </div>
              <div className="space-y-1">
                <StatLine label="Concernés" value={data.session_rattrapage?.nb_concernes ?? 0} />
                <StatLine label="Participants" value={data.session_rattrapage?.nb_participants ?? 0} />
                <StatLine label="Absents" value={data.session_rattrapage?.nb_absents ?? 0} muted />
              </div>
              <div>
                <div className="flex items-baseline justify-between text-[11px] mb-0.5">
                  <span className="text-gray-600 dark:text-gray-400">Taux participation</span>
                  <span className="font-mono font-bold text-orange-700 dark:text-orange-300">
                    {fmtPct(data.session_rattrapage?.taux_participation)}
                  </span>
                </div>
                <ProgressBar pct={data.session_rattrapage?.taux_participation ?? 0} color="bg-orange-500" />
              </div>
              <div className="pt-1 border-t border-orange-200 dark:border-orange-800 space-y-1">
                <StatLine
                  label={<><CheckCircle2 size={11} className="inline mr-1 text-green-600" />Admis après rattrapage</>}
                  value={data.session_rattrapage?.nb_admis ?? 0}
                />
              </div>
              <div>
                <div className="flex items-baseline justify-between text-[11px] mb-0.5">
                  <span className="text-gray-600 dark:text-gray-400">Taux admission</span>
                  <span className="font-mono font-bold text-green-700 dark:text-green-300">
                    {fmtPct(data.session_rattrapage?.taux_admission)}
                  </span>
                </div>
                <ProgressBar pct={data.session_rattrapage?.taux_admission ?? 0} color="bg-green-500" />
              </div>
            </div>

            {/* ─── Carte 3 : Rachat (simulation) ─── */}
            <div className="border border-purple-200 dark:border-purple-800 bg-purple-50/40 dark:bg-purple-900/10 rounded p-3 space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-purple-700 dark:text-purple-300">
                <LifeBuoy size={13} /> Rachat (simulation)
              </div>

              <div>
                <label className="block text-[11px] text-gray-600 dark:text-gray-400 mb-1">
                  Seuil de rachat : <span className="font-mono font-bold text-purple-700 dark:text-purple-300">{seuil.toFixed(2)}/20</span>
                </label>
                <input
                  type="range"
                  min={7}
                  max={10}
                  step={0.25}
                  value={seuil}
                  onChange={(e) => setSeuil(parseFloat(e.target.value))}
                  className="w-full accent-purple-600"
                />
                <div className="flex justify-between text-[10px] text-gray-500 mt-0.5">
                  <span>7</span><span>8</span><span>9</span><span>10</span>
                </div>
              </div>

              <div className="space-y-1 pt-1">
                <StatLine label="Total ajournés" value={data.rachat?.nb_total_ajournes ?? 0} muted />
                <StatLine
                  label="Éligibles au rachat"
                  value={
                    <span className="text-purple-700 dark:text-purple-300">
                      {data.rachat?.nb_eligibles_rachat ?? 0}
                    </span>
                  }
                />
              </div>
              <div>
                <div className="flex items-baseline justify-between text-[11px] mb-0.5">
                  <span className="text-gray-600 dark:text-gray-400">% sur ajournés</span>
                  <span className="font-mono font-bold text-purple-700 dark:text-purple-300">
                    {fmtPct(data.rachat?.taux_rachat_sur_ajournes)}
                  </span>
                </div>
                <ProgressBar pct={data.rachat?.taux_rachat_sur_ajournes ?? 0} color="bg-purple-500" />
              </div>
              <div className="pt-1 border-t border-purple-200 dark:border-purple-800 space-y-1">
                <StatLine
                  label="Admis si rachat appliqué"
                  value={
                    <span className="text-green-700 dark:text-green-300">
                      {data.rachat?.nb_admis_apres_rachat ?? 0}
                    </span>
                  }
                />
              </div>
              <div>
                <div className="flex items-baseline justify-between text-[11px] mb-0.5">
                  <span className="text-gray-600 dark:text-gray-400">Taux admission projeté</span>
                  <span className="font-mono font-bold text-green-700 dark:text-green-300">
                    {fmtPct(data.rachat?.taux_admission_apres_rachat)}
                  </span>
                </div>
                <ProgressBar pct={data.rachat?.taux_admission_apres_rachat ?? 0} color="bg-green-500" />
              </div>
            </div>
          </div>

          <div className="mt-3 text-[10px] text-gray-500 dark:text-gray-400 italic">
            Présent en session ordinaire : au moins 40 % de ses matières d'examen composées (copie associée
            ou note d'examen supérieure à 0), TP exclus ; au rattrapage : au moins une matière composée.
            Taux de la session ordinaire rapportés à l'effectif inscrit ; inscrits = admis (ordinaire + rattrapage)
            + ajournés + sans résultat. Taux du rattrapage rapportés aux étudiants concernés.
            Le rachat est une simulation : modifie le seuil pour voir combien d'ajournés seraient récupérés.
          </div>
        </>
      )}
    </div>
  );
};

export default StatsDeliberation;
